'use strict';

// external imports

// prime react imports

// shared imports
import {ChartDataSet} from '~/shared/src/utilities/chartDataSet';
import {ChartScale} from '~/shared/src/utilities/chartScale';
import {Column} from '~/shared/src/utilities/column';
import {Query} from '~/shared/src/utilities/query';
import dataExtraction from '~/shared/src/utilities/dataExtraction';
import {getDataStructureForSqlQuery} from '~/shared/src/utilities/modules/queryLoader';
import {
    DataDescriptor,
    DataStructure,
    extractDataObjectsFromDataStructure
} from '~/shared/src/utilities/dataStructure';
import dataUtils from '~/shared/src/utilities/dataUtils';

// frontend imports
import {ColumnDefinition} from '~/shared/src/validationSchemes/modules/overview';
import {__} from '~/shared/src/utilities/messageCatalog';
import metaModel from '~/shared/src/utilities/metaModel';


export const chartUtilities = {
    getQuery: ( dataContext : string, chartId : number ) : Query | undefined => {
        const chartItem = global.data[ dataContext ]?.items?.[ chartId ];

        return chartItem ? dataExtraction.extractQuery( chartItem, dataContext ) : undefined;
    },

    getDataSets: ( dataContext : string, chartId : number ) : ChartDataSet[] | undefined => {
        const chartItem = global.data[ dataContext ]?.items?.[ chartId ],
              query     = chartItem ? dataExtraction.extractQuery( chartItem, dataContext ) : undefined;

        let retVal : ChartDataSet[] | undefined = undefined;

        if ( chartItem ) {
            retVal = dataExtraction.extractChartDataSets( chartItem, dataContext, query?.dataStructure ?? getDataStructureForSqlQuery() );
        }

        return retVal;
    },

    getDataScales: ( dataContext : string, chartId : number ) : ChartScale[] | undefined => {
        const chartItem = global.data[ dataContext ]?.items?.[ chartId ];

        let retVal : ChartScale[] | undefined = undefined;

        if ( chartItem ) {
            retVal = dataExtraction.extractChartScales( chartItem, dataContext );
        }

        return retVal;
    },

    getRawColumn: ( dataSet : ChartDataSet, query : Query, schema : Schema ) => {
        let retVal : Column | undefined;

        if ( dataSet.dataDescriptor ) {
            let label : string;

            if ( dataSet.messages?.label ) {
                label = __( typeof dataSet.messages.label === 'string' ? dataSet.messages.label : dataSet.messages.label.key );
            }
            else {
                if ( query.type === 'DataStructure' ) {
                    label = __( dataUtils.getMetaModelLabelMsgId( dataSet.dataDescriptor, query.dataStructure, schema ) );

                    // get ItemType label if more than one DataObject
                    if ( dataSet.dataDescriptor?.dataObject?.item.objectType && extractDataObjectsFromDataStructure( query.dataStructure ).length > 1 ) {
                        const itemTypeLabel = metaModel[ schema ].getItemType( dataSet.dataDescriptor.dataObject.item.objectType )?.label;

                        if ( itemTypeLabel ) {
                            label = `${itemTypeLabel} ${label}`;
                        }
                    }
                }
                else {
                    label = dataSet.dataDescriptor.field;
                }

                if ( !label ) {
                    label = dataSet.data.name;
                }

                // get aggregate function label
                if ( dataSet.dataDescriptor.aggregate ) {
                    const chartDataSetAggregateFunctionPropertySet = metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( 'ChartDataSet', 'AggregateFunction' );

                    let aggregateLabel = chartDataSetAggregateFunctionPropertySet?.valueSetId
                        ? metaModel[ 'CFG' ].getValueSetValue( chartDataSetAggregateFunctionPropertySet.valueSetId, dataSet.dataDescriptor.aggregate )?.label
                        : undefined;

                    if ( !aggregateLabel ) {
                        aggregateLabel = dataSet.dataDescriptor.aggregate;
                    }

                    if ( aggregateLabel ) {
                        label = `${aggregateLabel} (${label})`;
                    }
                }
            }

            retVal = {
                data          : {
                    id       : dataSet.data.id,
                    key      : dataSet.data.key,
                    name     : dataSet.data.name,
                    valueType: dataSet.dataDescriptor.valueType ?? 'STRING',
                    order    : dataSet.data.order
                },
                dataDescriptor: dataSet.dataDescriptor,
                messages      : {
                    header: __( 'YN.base.frontend.chartDesigner.column.dataSet.title', { field: label } )
                }
            };
        }

        return retVal;
    },

    getColumnDefinition: ( dataDescriptor : DataDescriptor, header : string, dataStructure? : DataStructure, schema? : Schema ) : ColumnDefinition => {
        if ( !header && dataStructure && schema ) {
            header = dataUtils.getMetaModelLabelMsgId( dataDescriptor, dataStructure, schema );
        }

        if ( !header ) {
            header = dataDescriptor.alias;
        }

        return {
            field: dataDescriptor.alias,

            header,
            dataDescriptor: dataDescriptor,

            editor: false,
            filter: false,

            valueType: dataDescriptor.valueType ?? 'STRING',

            hidable     : false,
            hidden      : false,
            reorderable : true,
            resizeable  : true,
            searchColumn: false
        } as ColumnDefinition;
    }
}
